import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import { QRCodeSVG } from 'qrcode.react';
import Tutorial from './Tutorial';
import soundManager from '../utils/SoundManager';
import './HostScreen.css';

// Auto-detect server URL based on how client is accessed
function getServerURL() {
  // Check for explicit server URL first
  if (process.env.REACT_APP_SERVER_URL) {
    return process.env.REACT_APP_SERVER_URL;
  }
  
  const hostname = window.location.hostname;
  // Get port from env or default to 3002
  const port = process.env.REACT_APP_SERVER_PORT || '3002';
  
  // Special case: port 80 doesn't need to be in URL
  if (port === '80') {
    return `http://${hostname}`;
  }
  
  return `http://${hostname}:${port}`;
}

// Calculate at module load, but can be recalculated if needed
let SERVER_URL = getServerURL();

function HostScreen() {
  const [socket, setSocket] = useState(null);
  const [roomCode, setRoomCode] = useState('');
  const [players, setPlayers] = useState([]);
  const [currentRound, setCurrentRound] = useState(null);
  const [totalRounds, setTotalRounds] = useState(0);
  const [currentRoundIndex, setCurrentRoundIndex] = useState(0);
  const [gameState, setGameState] = useState('LOBBY');
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionNumber, setQuestionNumber] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [buzzedPlayers, setBuzzedPlayers] = useState([]);
  const [revealData, setRevealData] = useState(null);
  const [finalScores, setFinalScores] = useState(null);
  const [gamePacks, setGamePacks] = useState([]);
  const [selectedPacks, setSelectedPacks] = useState([]); // Array of selected pack IDs
  const [joinToast, setJoinToast] = useState(null); // Toast notification for player joins
  const [startVotes, setStartVotes] = useState(0);
  const [requiredVotes, setRequiredVotes] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const [pausedBy, setPausedBy] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [questionReady, setQuestionReady] = useState(false);
  const [roundCount, setRoundCount] = useState(6);
  const [showRoundSpinner, setShowRoundSpinner] = useState(false);
  const [spinnerRound, setSpinnerRound] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showWheelSpinner, setShowWheelSpinner] = useState(false);
  const [wheelPack, setWheelPack] = useState(null);
  const [roulettePosition, setRoulettePosition] = useState(0); // For roulette scrolling animation
  const [rouletteSelectedIndex, setRouletteSelectedIndex] = useState(-1); // Index of selected pack
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [bombHolder, setBombHolder] = useState(null); // For Bomb Pass mode
  const [stealAnimation, setStealAnimation] = useState(null); // For Point Steal animations
  const [eliminatedPlayers, setEliminatedPlayers] = useState([]); // For Final Countdown
  const [showRoundEndScoreboard, setShowRoundEndScoreboard] = useState(false); // Full scoreboard between rounds
  const [roundEndData, setRoundEndData] = useState(null); // Data for round-end scoreboard
  const [showTutorial, setShowTutorial] = useState(false); // Tutorial modal
  const [musicEnabled, setMusicEnabled] = useState(false); // Background music
  const [sfxEnabled, setSfxEnabled] = useState(true); // Sound effects
  const navigate = useNavigate();
  
  // Jokes for last place player
  const lastPlaceJokes = [
    "is bringing up the rear... way, way up the rear!",
    "is in last place, but at least they're consistent!",
    "might want to Google some trivia facts tonight!",
    "is proving that it's not about winning, it's about participating!",
    "is making everyone else feel better about themselves!",
    "could use a study buddy... or ten!",
    "is the reason we don't have participation trophies!",
    "is really embracing the 'learning experience'!",
    "might be playing a different game entirely!",
    "is proof that someone has to be last!",
  ];
  
  const getRandomJoke = () => lastPlaceJokes[Math.floor(Math.random() * lastPlaceJokes.length)];
  const lastRoundIdRef = useRef(null);
  const hostScreenRef = useRef(null);
  // Simplified TTS - direct SpeechSynthesis API
  const ttsSpokenRef = useRef(null);
  const currentUtteranceRef = useRef(null);
  const audioUnlockRef = useRef(false);
  const speakTTSRef = useRef(null);
  const unlockAudioRef = useRef(null);
  const persistentAudioContextRef = useRef(null);
  const reusableAudioRef = useRef(null);
  const roomCodeRef = useRef(''); // Keep roomCode in ref for event handlers
  const socketRef = useRef(null); // Keep socket in ref for event handlers
  const nextQuestionRef = useRef(null); // Pre-load next question for TTS
  const preloadTTSRef = useRef(null); // Pre-loaded TTS utterance
  
  // Unlock audio (for iOS Safari) - defined first
  const unlockAudio = React.useCallback(async () => {
    if (audioUnlockRef.current) {
      return true;
    }
    
    try {
      // Create persistent AudioContext (keep it alive)
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext && !persistentAudioContextRef.current) {
        persistentAudioContextRef.current = new AudioContext();
        console.log('✅ Created persistent AudioContext');
      }
      
      // Resume AudioContext if suspended
      if (persistentAudioContextRef.current && persistentAudioContextRef.current.state === 'suspended') {
        await persistentAudioContextRef.current.resume();
        console.log('✅ AudioContext resumed');
      }
      
      // Method 1: Create and play a silent audio buffer (required for iOS)
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (AudioContext) {
          const ctx = new AudioContext();
          const buffer = ctx.createBuffer(1, 1, 22050); // 1 channel, 1 sample, 22050 Hz
          const source = ctx.createBufferSource();
          source.buffer = buffer;
          source.connect(ctx.destination);
          source.start(0);
          await new Promise(resolve => setTimeout(resolve, 100));
          source.stop();
          console.log('✅ Silent audio buffer played - audio unlocked');
        }
      } catch (silentError) {
        console.warn('Silent audio buffer failed:', silentError);
      }
      
      // Create reusable audio element (reuse this instead of creating new ones)
      if (!reusableAudioRef.current) {
        reusableAudioRef.current = new Audio();
        reusableAudioRef.current.volume = 1;
        
        // Play and immediately pause to "activate" it with user gesture (critical for iOS)
        try {
          const activatePromise = reusableAudioRef.current.play();
          if (activatePromise !== undefined) {
            await activatePromise;
            await reusableAudioRef.current.pause();
            console.log('✅ Reusable audio element activated');
          }
        } catch (activateError) {
          console.warn('⚠️ Audio activation failed:', activateError);
        }
      }
      
      audioUnlockRef.current = true;
      setAudioEnabled(true);
      
      // Also init SoundManager for sound effects
      await soundManager.init();
      
      return true;
    } catch (error) {
      console.warn('Audio unlock failed:', error);
      audioUnlockRef.current = true;
      setAudioEnabled(true);
      return false;
    }
  }, []);

  // Update unlockAudio ref
  React.useEffect(() => {
    unlockAudioRef.current = unlockAudio;
  }, [unlockAudio]);

  // Preload voices (for faster TTS)
  useEffect(() => {
    if ('speechSynthesis' in window) {
      // Trigger voice loading
      speechSynthesis.getVoices();
      // iOS sometimes needs this event
      if (speechSynthesis.onvoiceschanged !== undefined) {
        speechSynthesis.onvoiceschanged = () => {
          console.log('✅ Voices loaded:', speechSynthesis.getVoices().length);
        };
      }
    }
  }, []);

  // Get preferred natural voice
  const getPreferredVoice = React.useCallback(() => {
    if (!('speechSynthesis' in window)) return null;
    
    const voices = speechSynthesis.getVoices();
    // Prefer natural-sounding voices (in order of preference)
    const preferredNames = [
      'Samantha',           // macOS - very natural
      'Karen',              // macOS - natural
      'Victoria',           // macOS - natural
      'Google US English',  // Chrome - natural
      'Microsoft Zira',     // Windows - natural
      'Microsoft Mark',     // Windows - natural
      'en-US',              // Generic US English
      'en-GB'               // Generic UK English
    ];
    
    // Try to find preferred voice (prioritize more natural-sounding voices)
    for (const name of preferredNames) {
      const voice = voices.find(v => 
        v.name.includes(name) || 
        v.lang.includes(name) ||
        (name === 'en-US' && v.lang.startsWith('en'))
      );
      if (voice) {
        console.log('✅ Selected voice:', voice.name, voice.lang);
        return voice;
      }
    }
    
    // Try to find high-quality voices (prefer neural/enhanced voices)
    const neuralVoices = voices.filter(v => 
      v.lang.startsWith('en') && (
        v.name.toLowerCase().includes('neural') ||
        v.name.toLowerCase().includes('enhanced') ||
        v.name.toLowerCase().includes('premium') ||
        v.name.toLowerCase().includes('natural')
      )
    );
    if (neuralVoices.length > 0) {
      console.log('✅ Using neural/enhanced voice:', neuralVoices[0].name);
      return neuralVoices[0];
    }
    
    // Fallback to first English voice
    const englishVoice = voices.find(v => v.lang.startsWith('en'));
    if (englishVoice) {
      console.log('✅ Using fallback voice:', englishVoice.name);
      return englishVoice;
    }
    
    return voices[0] || null;
  }, []);

  // Helper function to actually start speaking (defined first so speakTTS can use it)
  const startSpeaking = React.useCallback((text, options, resolve) => {
    const utterance = new SpeechSynthesisUtterance(text);
    
    // Get preferred voice
    const voice = getPreferredVoice();
    if (voice) {
      utterance.voice = voice;
      console.log('✅ Using voice:', voice.name, voice.lang);
    } else {
      console.warn('⚠️ No preferred voice found, using default');
    }
    
    // Set options for more natural, fluid speech - match instruction voice
    utterance.rate = options.rate || 0.85; // Match instruction voice for consistency
    utterance.pitch = options.pitch || 0.95; // Match instruction voice for consistency
    utterance.volume = options.volume || 1.0;
    utterance.lang = 'en-US';
    
    // Process text for more natural speech with pauses
    // Add slight pauses after punctuation for better flow
    let processedText = text
      // Add pauses after periods, exclamations, questions (but not if at end)
      .replace(/\.([^\s])/g, '. $1') // Space after period if not followed by space
      .replace(/\!([^\s])/g, '! $1') // Space after exclamation
      .replace(/\?([^\s])/g, '? $1') // Space after question
      // Ensure commas have proper spacing
      .replace(/\,([^\s])/g, ', $1'); // Space after comma
    
    // Use processed text
    utterance.text = processedText;
    
    let resolved = false;
    let actuallyStarted = false;
    let startCheckTimeout = null;
    
    // Estimate speech duration
    const wordCount = text.split(/\s+/).length;
    const estimatedDuration = (wordCount / 150) * 60 * 1000 / (options.rate || 0.95);
    const maxWaitTime = Math.min(Math.max(estimatedDuration + 3000, 8000), 15000); // 8-15 seconds for questions
    
    // Fallback timeout
    const timeoutId = setTimeout(() => {
      if (!resolved) {
        console.warn(`⚠️ TTS timeout after ${maxWaitTime}ms. Started: ${actuallyStarted}, Speaking: ${speechSynthesis.speaking}`);
        resolved = true;
        if (startCheckTimeout) clearTimeout(startCheckTimeout);
        resolve();
      }
    }, maxWaitTime);
    
    utterance.onstart = () => {
      actuallyStarted = true;
      console.log('✅ TTS STARTED speaking:', text.substring(0, 50));
      if (startCheckTimeout) clearTimeout(startCheckTimeout);
    };
    
    utterance.onend = () => {
      if (!resolved) {
        console.log('✅ TTS FINISHED (onend event)');
        resolved = true;
        clearTimeout(timeoutId);
        if (startCheckTimeout) clearTimeout(startCheckTimeout);
        resolve();
      }
    };
    
    utterance.onerror = (error) => {
      if (!resolved) {
        // "interrupted" errors are expected when cancelling speech - don't log as error
        if (error.error === 'interrupted') {
          console.log('ℹ️ TTS interrupted (expected when cancelling previous speech)');
        } else {
          console.error('❌ TTS ERROR:', error);
          console.error('Error details:', {
            error: error.error,
            type: error.type,
            charIndex: error.charIndex
          });
        }
        resolved = true;
        clearTimeout(timeoutId);
        if (startCheckTimeout) clearTimeout(startCheckTimeout);
        resolve();
      }
    };
    
    // Check if it actually started after 1 second
    startCheckTimeout = setTimeout(() => {
      if (!actuallyStarted && !resolved) {
        console.warn('⚠️ TTS did not start after 1 second. Retrying...');
        console.warn('SpeechSynthesis state:', {
          speaking: speechSynthesis.speaking,
          pending: speechSynthesis.pending,
          paused: speechSynthesis.paused
        });
        
        // Try to resume if paused
        if (speechSynthesis.paused) {
          console.log('🔄 Resuming paused speechSynthesis');
          speechSynthesis.resume();
        }
        
        // Retry speaking
        try {
          speechSynthesis.cancel();
          setTimeout(() => {
            speechSynthesis.speak(utterance);
            console.log('🔄 Retry: Speaking with native TTS');
          }, 100);
        } catch (retryError) {
          console.error('❌ Retry failed:', retryError);
        }
      }
    }, 1000);
    
    // Ensure voices are loaded
    const voices = speechSynthesis.getVoices();
    if (voices.length === 0) {
      console.log('⏳ Waiting for voices to load...');
      const voicesHandler = () => {
        console.log('✅ Voices loaded:', speechSynthesis.getVoices().length);
        const voice = getPreferredVoice();
        if (voice) utterance.voice = voice;
        try {
          speechSynthesis.speak(utterance);
          console.log('🔊 Speaking after voice load');
        } catch (speakError) {
          console.error('❌ Failed to speak after voice load:', speakError);
          if (!resolved) {
            resolved = true;
            clearTimeout(timeoutId);
            if (startCheckTimeout) clearTimeout(startCheckTimeout);
            resolve();
          }
        }
        speechSynthesis.onvoiceschanged = null; // Remove handler
      };
      speechSynthesis.onvoiceschanged = voicesHandler;
    } else {
      try {
        speechSynthesis.speak(utterance);
        console.log('🔊 Speaking with native TTS immediately');
      } catch (speakError) {
        console.error('❌ Failed to speak:', speakError);
        if (!resolved) {
          resolved = true;
          clearTimeout(timeoutId);
          if (startCheckTimeout) clearTimeout(startCheckTimeout);
          resolve();
        }
      }
    }
    
    // Store current utterance
    currentUtteranceRef.current = utterance;
  }, [getPreferredVoice]);

  // TTS function - uses ONLY server-side TTS for consistent voice
  // No browser fallback to avoid voice inconsistency
  const speakTTS = React.useCallback((text, options = {}) => {
    return new Promise((resolve) => {
      if (!text || text.trim() === '') {
        console.warn('⚠️ Empty text provided to TTS');
        resolve();
        return;
      }

      // Server-side TTS only (consistent voice)
      const serverUrl = getServerURL();
      const rate = options.rate || 0.85;
      const pitch = options.pitch || 0.95;
      const ttsUrl = `${serverUrl}/api/tts?text=${encodeURIComponent(text)}&rate=${rate}&pitch=${pitch}`;
      
      console.log('🔊 Server TTS:', text.substring(0, 50) + '...');
      
      // Create audio element for server TTS
      const audio = reusableAudioRef.current || new Audio();
      if (!reusableAudioRef.current) {
        reusableAudioRef.current = audio;
      }
      
      // Unlock audio first
      unlockAudioRef.current?.();
      
      let resolved = false;
      const timeoutId = setTimeout(() => {
        if (!resolved) {
          console.warn('⚠️ Server TTS timeout - skipping to avoid voice inconsistency');
          resolved = true;
          resolve(); // Just resolve, don't fall back to browser TTS
        }
      }, 15000); // 15 second timeout
      
      const handleError = () => {
        if (!resolved) {
          console.warn('⚠️ Server TTS error - skipping');
          resolved = true;
          clearTimeout(timeoutId);
          resolve(); // Just resolve, don't fall back
        }
      };
      
      audio.onloadeddata = () => {
        if (!resolved) {
          console.log('✅ Server TTS loaded');
          audio.play().catch((error) => {
            console.warn('⚠️ Server TTS play failed:', error);
            handleError();
          });
        }
      };
      
      audio.oncanplaythrough = () => {
        if (!resolved && audio.readyState >= 3) {
          audio.play().catch((error) => {
            console.warn('⚠️ Server TTS play failed:', error);
            handleError();
          });
        }
      };
      
      audio.onended = () => {
        if (!resolved) {
          console.log('✅ Server TTS finished');
          resolved = true;
          clearTimeout(timeoutId);
          resolve();
        }
      };
      
      audio.onerror = (error) => {
        console.warn('⚠️ Server TTS audio error:', error);
        handleError();
      };
      
      // Set source and load
      audio.src = ttsUrl;
      audio.volume = 1.0;
      audio.load();
    });
  }, []);

  // Update ref whenever speakTTS changes
  React.useEffect(() => {
    speakTTSRef.current = speakTTS;
  }, [speakTTS]);

  useEffect(() => {
    // Recalculate server URL in case env vars changed
    const serverUrl = getServerURL();
    console.log('🔌 Connecting to server at:', serverUrl);
    console.log('📍 Current location:', window.location.href);
    console.log('🌐 Hostname:', window.location.hostname);
    console.log('🔧 REACT_APP_SERVER_PORT:', process.env.REACT_APP_SERVER_PORT);
    console.log('🔧 REACT_APP_SERVER_URL:', process.env.REACT_APP_SERVER_URL);
    
    const newSocket = io(serverUrl, {
      transports: ['polling', 'websocket'], // Try polling first, then websocket
      timeout: 20000, // Increased timeout
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10, // More reconnection attempts
      forceNew: true,
      upgrade: true,
      rememberUpgrade: false
    });
    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('✅ Host connected to server:', serverUrl);
      console.log('✅ Socket ID:', newSocket.id);
      setSocket(newSocket);
      socketRef.current = newSocket; // Update ref as well
    });

    newSocket.on('connect_error', (error) => {
      console.error('❌ Host connection error:', error);
      console.error('❌ Attempted URL:', serverUrl);
      console.error('❌ Make sure the server is running on:', serverUrl);
      console.error('❌ Start the server with: npm run server');
      console.error('❌ Check if server is accessible at:', serverUrl);
    });

    newSocket.on('disconnect', (reason) => {
      console.warn('Host disconnected:', reason);
    });

    newSocket.on('reconnect', (attemptNumber) => {
      console.log('✅ Reconnected after', attemptNumber, 'attempts');
    });

    newSocket.on('reconnect_error', (error) => {
      console.error('❌ Reconnection error:', error);
    });

    newSocket.on('game-created', ({ roomCode: code, gamePacks: packs, gameTypes: types }) => {
      console.log('Game created with room code:', code);
      console.log('Received packs:', packs?.length, packs);
      setRoomCode(code);
      roomCodeRef.current = code; // Update ref as well
      const allPacks = packs || [];
      setGamePacks(allPacks);
      
      // Default to ALL packs selected
      const allPackIds = allPacks.map(p => p.id);
      setSelectedPacks(allPackIds);
      
      // Auto-select all packs on server
      if (allPackIds.length > 0) {
        setTimeout(() => {
          newSocket.emit('select-packs', { roomCode: code, packIds: allPackIds });
        }, 100);
      }
      
      console.log('Default selected all packs:', allPackIds.length);
    });

    newSocket.on('player-joined', ({ playerName }) => {
      console.log('🎉 Player joined:', playerName);
      
      // Play join sound
      soundManager.playJoin();
      
      // Show toast notification
      setJoinToast({ name: playerName, visible: true });
      setTimeout(() => {
        setJoinToast(prev => prev ? { ...prev, visible: false } : null);
      }, 3000);
      
      // Announce with TTS
      if (speakTTSRef.current) {
        speakTTSRef.current(`${playerName} joined the game`, { rate: 0.85, pitch: 0.95 });
      }
    });

    newSocket.on('room-state', (state) => {
      setPlayers(state.players || []);
      setGameState(state.state);
      // Only update currentQuestion if provided, or preserve it during QUESTION_INTRO
      // (server doesn't send question during QUESTION_INTRO, but we need it for TTS)
      if (state.currentQuestion !== null && state.currentQuestion !== undefined) {
        setCurrentQuestion(state.currentQuestion);
      } else if (state.state !== 'QUESTION_INTRO') {
        // Clear question if not in QUESTION_INTRO and server says it's null
        setCurrentQuestion(null);
      }
      // Otherwise, keep currentQuestion during QUESTION_INTRO for TTS
      setQuestionNumber(state.questionNumber || 0);
      setTotalQuestions(state.totalQuestions || 0);
      setSelectedPacks(state.selectedPacks || []); // Sync selectedPacks from server
      setStartVotes(state.startVoteCount || 0);
      setIsPaused(state.isPaused || false);
      setPausedBy(state.pausedBy);
      setRequiredVotes(Math.max(1, Math.ceil((state.players?.length || 0) / 2)));
      setCurrentRound(state.currentRound || null);
      setTotalRounds(state.totalRounds || 0);
      if (typeof state.currentRoundIndex === 'number') {
        setCurrentRoundIndex(state.currentRoundIndex);
      }
      if (typeof state.roundCount === 'number') {
        setRoundCount(state.roundCount);
      }

      // Detect round change to trigger spinner
      const newRoundId = state.currentRound?.id;
      if (newRoundId && newRoundId !== lastRoundIdRef.current && state.state === 'QUESTION_INTRO') {
        lastRoundIdRef.current = newRoundId;
        setSpinnerRound(state.currentRound);
        setShowRoundSpinner(true);
        setTimeout(() => setShowRoundSpinner(false), 2200);
      }
    });

    newSocket.on('packs-selected', ({ packIds }) => {
      setSelectedPacks(packIds);
    });

    newSocket.on('pack-wheel-spin', ({ packId, pack, roundIndex, allPacks }) => {
      console.log('🎡 Roulette spin event received:', {
        packId,
        packName: pack?.name,
        packIcon: pack?.icon,
        roundIndex,
        allPacksLength: allPacks?.length,
        allPacksIds: allPacks?.map(p => p?.id || p?.name)
      });
      
      if (!allPacks || !Array.isArray(allPacks) || allPacks.length === 0) {
        console.error('⚠️ No packs provided for roulette or invalid format:', allPacks);
        return;
      }
      
      if (!pack || !packId) {
        console.error('⚠️ No pack or packId provided:', { pack, packId });
        return;
      }
      
      // Find the index of the selected pack (try both id and name matching)
      let selectedIndex = allPacks.findIndex(p => p?.id === packId);
      if (selectedIndex === -1) {
        // Fallback: try matching by name
        selectedIndex = allPacks.findIndex(p => p?.name === pack?.name);
      }
      
      if (selectedIndex === -1) {
        console.error('⚠️ Selected pack not found in allPacks. packId:', packId, 'allPacks:', allPacks.map(p => ({ id: p?.id, name: p?.name })));
        // Still try to show the wheel with index 0
        selectedIndex = 0;
      }
      
      console.log('✅ Setting roulette state - selectedIndex:', selectedIndex, 'pack:', pack?.name);
      
      // Hide round spinner if showing (roulette should show first)
      setShowRoundSpinner(false);
      
      // Ensure pack has id
      const packWithId = { ...pack, id: pack.id || packId };
      
      setWheelPack({ packId, pack: packWithId, roundIndex, allPacks });
      setRouletteSelectedIndex(selectedIndex);
      setRoulettePosition(0); // Reset position
      setShowWheelSpinner(true);
      
      console.log('✅ Roulette wheel should now be visible. State:', {
        showWheelSpinner: true,
        wheelPack: { packId, packName: packWithId?.name, allPacksLength: allPacks.length }
      });
      
      // Start roulette animation - scroll through packs multiple times, then slow down
      const totalPacks = allPacks.length;
      const scrollCycles = 3; // Scroll through all packs 3 times
      const itemWidth = 200; // Width of each pack item in pixels
      const spacing = 20; // Gap between items
      const totalWidth = itemWidth + spacing;
      
      // Animation phases: fast scroll -> slow down -> stop at selected
      const fastDuration = 2000; // Fast scrolling phase (2 seconds)
      const slowDuration = 1500; // Slow down phase (1.5 seconds)
      const totalDuration = fastDuration + slowDuration;
      
      // Start fast scrolling
      let startTime = Date.now();
      let animationFrameId = null;
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        
        if (elapsed < fastDuration) {
          // Fast scrolling phase - linear speed
          const progress = elapsed / fastDuration;
          const fastPosition = progress * (scrollCycles * totalPacks * totalWidth);
          setRoulettePosition(-fastPosition);
          animationFrameId = requestAnimationFrame(animate);
        } else if (elapsed < totalDuration) {
          // Slow down phase - ease out
          const slowProgress = (elapsed - fastDuration) / slowDuration;
          // Ease out cubic function for smooth deceleration
          const easeOut = 1 - Math.pow(1 - slowProgress, 3);
          const fastPosition = scrollCycles * totalPacks * totalWidth;
          const slowPosition = selectedIndex * totalWidth;
          const totalPosition = fastPosition + (slowPosition - fastPosition) * easeOut;
          setRoulettePosition(-totalPosition);
          animationFrameId = requestAnimationFrame(animate);
        } else {
          // Animation complete - land on selected pack
          setRoulettePosition(-selectedIndex * totalWidth);
          
          // Topic is visually shown on wheel - don't announce via TTS
          // This prevents overlap with round instructions
          console.log('🎯 Topic selected:', pack?.name);
          
          // Hide wheel after showing result (3 seconds after landing)
          setTimeout(() => {
            setShowWheelSpinner(false);
          }, 3000);
        }
      };
      
      // Start animation
      animationFrameId = requestAnimationFrame(animate);
      
      // Cleanup function to cancel animation if component unmounts
      return () => {
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
      };
    });

    newSocket.on('vote-updated', ({ voteCount, requiredVotes: req, error }) => {
      setStartVotes(voteCount);
      setRequiredVotes(req);
      if (error) {
        console.warn('Vote error:', error);
        // Could show a toast/alert here
      }
    });


    // Vote updated handler moved above

    newSocket.on('game-paused', ({ isPaused: paused, pausedBy: by }) => {
      setIsPaused(paused);
      setPausedBy(by);
    });

    newSocket.on('round-started', async ({ roundIndex, totalRounds, currentRound, packId, pack }) => {
      console.log('🎯 Round started:', currentRound?.name);
      
      // Hide roulette wheel if still showing
      setShowWheelSpinner(false);
      
      // Show round transition screen with instructions
      setCurrentRound(currentRound);
      setShowRoundSpinner(true);
      setSpinnerRound({ roundIndex, totalRounds, currentRound, packId, pack });
      
      // Speak round instructions and wait for completion
      if (currentRound && speakTTSRef.current) {
        let announcement = `Round ${roundIndex + 1}: ${currentRound.name}. ${currentRound.description}`;
        
        // Add bomb holder info for Bomb Pass mode
        if (currentRound.mode === 'bomb_pass') {
          // Reset bomb holder - will be updated when bomb-holder-changed event fires
          setBombHolder(null);
        }
        
        // Reset mode-specific state
        setStealAnimation(null);
        setEliminatedPlayers([]);
        
        // Helper function to send completion signal (with retries)
        // Use newSocket and roomCodeRef to ensure we have the current values
        const sendCompletionSignal = (source) => {
          const currentRoomCode = roomCodeRef.current;
          if (newSocket && currentRoomCode) {
            console.log(`📤 Emitting tts-complete signal for round instructions (${source})`);
            newSocket.emit('tts-complete', { roomCode: currentRoomCode, type: 'round-instructions' });
          } else {
            console.warn(`⚠️ Cannot send completion signal: socket=${!!newSocket}, roomCode=${currentRoomCode}`);
          }
        };
        
        // Single fallback signal only if TTS takes too long (20 seconds max)
        // This ensures full instructions are spoken before moving on
        const fallbackTimeout = setTimeout(() => {
          console.warn('⚠️ Round instructions TTS taking longer than expected, signaling completion');
          sendCompletionSignal('fallback-timeout');
        }, 20000); // 20 seconds max for round instructions (increased to prevent cutoff)
        
        // Send immediate signal if TTS isn't available
        if (!speakTTSRef.current) {
          console.warn('⚠️ TTS not available for round instructions, signaling completion immediately');
          clearTimeout(fallbackTimeout);
          sendCompletionSignal('immediate-no-tts');
          return;
        }
        
        try {
          // Use different voice settings for instructions (more formal/clear)
          await speakTTSRef.current(announcement, { rate: 0.85, pitch: 0.95, priority: 'high' });
          console.log('✅ Round announcement complete - full instructions spoken');
          
          // Clear fallback timeout since TTS completed normally
          clearTimeout(fallbackTimeout);
          
          // Signal server that round instructions TTS is complete
          sendCompletionSignal('normal-completion');
        } catch (error) {
          console.error('Round TTS error:', error);
          
          // Clear fallback timeout since we're handling error
          clearTimeout(fallbackTimeout);
          
          // Signal server even on error
          sendCompletionSignal('error-case');
        }
      }
      
      // Round instructions screen will hide when server moves to next phase
      // (controlled by server waiting for TTS completion signal)
    });

    newSocket.on('bomb-holder-changed', ({ playerName, isNewRound = false }) => {
      setBombHolder(playerName);
      if (speakTTSRef.current) {
        const announcement = isNewRound 
          ? `${playerName} starts with the bomb!`
          : `The bomb is now with ${playerName}!`;
        // Use instruction voice for bomb announcements
        speakTTSRef.current(announcement, { rate: 0.85, pitch: 0.95 });
      }
    });
    
    newSocket.on('players-eliminated', ({ eliminated }) => {
      setEliminatedPlayers(eliminated);
      // Play elimination sound
      if (speakTTSRef.current && eliminated.length > 0) {
        const names = eliminated.join(', ');
        // Use instruction voice for eliminations
        speakTTSRef.current(`${names} ${eliminated.length === 1 ? 'has been eliminated' : 'have been eliminated'}!`, { rate: 0.85, pitch: 0.95 });
      }
    });

    newSocket.on('game-started', ({ question, questionNumber, totalQuestions, currentRound }) => {
      // Reset TTS tracking for new game/round so first question speaks
      ttsSpokenRef.current = null;
      
      setCurrentQuestion(question);
      setQuestionNumber(questionNumber);
      setTotalQuestions(totalQuestions);
      setBuzzedPlayers([]);
      setCountdown(3);
      if (currentRound) {
        setCurrentRound(currentRound);
        // Don't speak here - will be spoken by round-started event
      }
      
      console.log('🎮 Game started, question:', question?.question?.substring(0, 50));
      
      // Pre-load TTS for first question if available
      if (question && 'speechSynthesis' in window) {
        const questionId = question.id || `${questionNumber}-${question.question}`;
        nextQuestionRef.current = { id: questionId, question: question.question };
        
        // Create utterance but don't speak it yet
        const utterance = new SpeechSynthesisUtterance(question.question);
        const voices = speechSynthesis.getVoices();
        const preferredVoice = voices.find(v => 
          v.name.includes('Google US English') || 
          v.name.includes('Samantha') || 
          v.name.includes('Karen') ||
          v.lang.startsWith('en')
        );
        if (preferredVoice) utterance.voice = preferredVoice;
        utterance.rate = 0.85; // Match instruction voice
        utterance.pitch = 0.95; // Match instruction voice
        utterance.volume = 1.0;
        utterance.lang = 'en-US';
        
        // Store for later use
        preloadTTSRef.current = utterance;
        console.log('⚡ Pre-loaded TTS for first question');
      }
    });

    newSocket.on('answering-started', ({ timeLimit }) => {
      setBuzzedPlayers([]);
      setRevealData(null);
      setCountdown(timeLimit);
      setQuestionReady(false);
    });

    newSocket.on('question-ready', ({ ready }) => {
      setQuestionReady(ready);
    });

    newSocket.on('buzz-received', ({ playerName, buzzOrder }) => {
      setBuzzedPlayers(prev => [...prev, { name: playerName, order: buzzOrder }]);
      
      // Announce first answer submission with TTS (low priority - don't interrupt question TTS)
      const announcement = buzzOrder === 1 
        ? `${playerName} answered first!`
        : buzzOrder === 2 
        ? `${playerName} answered second!`
        : buzzOrder === 3
        ? `${playerName} answered third!`
        : `${playerName} submitted an answer!`;
      
      console.log('🔊 Announcing:', announcement);
      
      // Only announce if we have TTS and question TTS isn't currently speaking
      if (speakTTSRef.current && !window.questionTTSInProgress) {
        speakTTSRef.current(announcement, { rate: 0.85, pitch: 0.95 });
      }
    });

    newSocket.on('reveal-answer', (data) => {
      setRevealData(data);
      setCountdown(5);
      
      // Handle Point Steal animation
      if (data.steal) {
        setStealAnimation(data.steal);
        // Play steal sound (would need to add audio context for host)
        setTimeout(() => setStealAnimation(null), 3000); // Clear after 3 seconds
      }
      
      // Handle Final Countdown eliminations
      if (data.eliminated && data.eliminated.length > 0) {
        setEliminatedPlayers(data.eliminated);
      }
    });

    // All players answered - speed up countdown
    newSocket.on('all-answered', ({ speedUp, revealIn }) => {
      console.log('🎉 All players answered! Speeding up countdown...');
      if (speedUp && revealIn) {
        setCountdown(revealIn); // Set countdown to 2 seconds
        // Announce only if question TTS is complete
        if (speakTTSRef.current && !window.questionTTSInProgress) {
          speakTTSRef.current('All players answered!', { rate: 0.85, pitch: 0.95 });
        }
      }
    });

    newSocket.on('next-question', ({ question, questionNumber, totalQuestions, currentRound }) => {
      setCurrentQuestion(question);
      setQuestionNumber(questionNumber);
      setTotalQuestions(totalQuestions);
      setBuzzedPlayers([]);
      setRevealData(null);
      setCountdown(3);
      if (currentRound) {
        setCurrentRound(currentRound);
      }
      
      // Pre-load TTS for next question if available (optimization for smooth transitions)
      // This happens while current question is being displayed
      if (question && 'speechSynthesis' in window) {
        const questionId = question.id || `${questionNumber}-${question.question}`;
        nextQuestionRef.current = { id: questionId, question: question.question };
        
        // Create utterance but don't speak it yet
        const utterance = new SpeechSynthesisUtterance(question.question);
        const voices = speechSynthesis.getVoices();
        const preferredVoice = voices.find(v => 
          v.name.includes('Google US English') || 
          v.name.includes('Samantha') || 
          v.name.includes('Karen') ||
          v.lang.startsWith('en')
        );
        if (preferredVoice) utterance.voice = preferredVoice;
        utterance.rate = 0.85; // Match instruction voice
        utterance.pitch = 0.95; // Match instruction voice
        utterance.volume = 1.0;
        utterance.lang = 'en-US';
        
        // Store for later use
        preloadTTSRef.current = utterance;
        console.log('⚡ Pre-loaded TTS for next question');
      }
    });

    newSocket.on('leader-announcement', ({ leaderName, leadPoints, leaderScore }) => {
      if (speakTTSRef.current) {
        let announcement = `${leaderName} is in the lead`;
        if (leadPoints > 0) {
          announcement += ` with ${leadPoints} point${leadPoints === 1 ? '' : 's'}`;
        }
        speakTTSRef.current(announcement, { rate: 0.85, pitch: 0.95 });
      }
    });

    // Round-end scoreboard with full scores and jokes
    newSocket.on('round-ended', async ({ roundIndex, totalRounds, scores, isLastRound }) => {
      console.log('🏆 Round ended, showing scoreboard');
      
      // Sort scores
      const sortedScores = [...scores].sort((a, b) => b.score - a.score);
      setRoundEndData({ roundIndex, totalRounds, scores: sortedScores, isLastRound });
      setShowRoundEndScoreboard(true);
      
      // Announce scores with TTS
      if (speakTTSRef.current && sortedScores.length > 0) {
        const roundNum = roundIndex + 1;
        let announcement = `End of round ${roundNum}. `;
        
        // Announce top 3
        if (sortedScores.length >= 1) {
          announcement += `In first place, ${sortedScores[0].name} with ${sortedScores[0].score} points. `;
        }
        if (sortedScores.length >= 2) {
          announcement += `In second, ${sortedScores[1].name} with ${sortedScores[1].score} points. `;
        }
        if (sortedScores.length >= 3) {
          announcement += `In third, ${sortedScores[2].name} with ${sortedScores[2].score} points. `;
        }
        
        // Joke about last place (if more than 3 players)
        if (sortedScores.length > 3) {
          const lastPlace = sortedScores[sortedScores.length - 1];
          announcement += `And in last place, ${lastPlace.name} ${getRandomJoke()}`;
        }
        
        await speakTTSRef.current(announcement, { rate: 0.85, pitch: 0.95 });
      }
      
      // Hide scoreboard after delay (server will send next event)
      setTimeout(() => {
        setShowRoundEndScoreboard(false);
        setRoundEndData(null);
      }, 8000);
    });

    newSocket.on('game-ended', ({ finalScores }) => {
      setFinalScores(finalScores);
    });

    newSocket.emit('create-game');

    return () => {
      newSocket.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Socket setup should only run once on mount

  // Countdown timer
  useEffect(() => {
    if (countdown !== null && countdown > 0 && !isPaused) {
      const timer = setInterval(() => {
        setCountdown(prev => prev > 0 ? prev - 1 : null);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [countdown, isPaused]);


  // Speak question when it appears
  useEffect(() => {
    if (currentQuestion && gameState === 'QUESTION_INTRO') {
      const questionId = currentQuestion.id || `${questionNumber}-${currentQuestion.question}`;

      console.log('🔊 Question TTS useEffect triggered:', {
        hasQuestion: !!currentQuestion,
        questionText: currentQuestion.question?.substring(0, 50),
        gameState,
        questionId,
        alreadySpoken: ttsSpokenRef.current === questionId
      });

      // If we've already spoken this question, don't repeat it
      if (ttsSpokenRef.current === questionId) {
        console.log('⏭️ Skipping - already spoken this question');
        return;
      }

      // Wait a moment for question to display, then speak
      const timeoutId = setTimeout(async () => {
        // Mark as spoken immediately to prevent duplicate calls
        ttsSpokenRef.current = questionId;
        
        // Mark question TTS as in progress to prevent buzz announcements from overlapping
        window.questionTTSInProgress = true;
        
        // Helper function to send completion signal (with retries)
        // Use socketRef and roomCodeRef to ensure we have the current values
        const sendCompletionSignal = (source) => {
          const currentSocket = socketRef.current;
          const currentRoomCode = roomCodeRef.current;
          if (currentSocket && currentRoomCode) {
            console.log(`📤 Emitting tts-complete signal for question (${source})`);
            currentSocket.emit('tts-complete', { roomCode: currentRoomCode, type: 'question' });
          } else {
            console.warn(`⚠️ Cannot send completion signal: socket=${!!currentSocket}, roomCode=${currentRoomCode}`);
          }
        };
        
        // Single fallback signal only if TTS takes too long (12 seconds max for questions)
        const fallbackTimeout = setTimeout(() => {
          console.warn('⚠️ Question TTS taking longer than expected, signaling completion');
          window.questionTTSInProgress = false;
          sendCompletionSignal('fallback-timeout');
        }, 12000); // 12 seconds max for questions
        
        // Check if we have a pre-loaded TTS for this question
        if (preloadTTSRef.current && nextQuestionRef.current?.id === questionId) {
          console.log('⚡ Using pre-loaded TTS for question');
          try {
            // Use pre-loaded utterance
            const utterance = preloadTTSRef.current;
            preloadTTSRef.current = null; // Clear after use
            nextQuestionRef.current = null;
            
            // Set up completion handler
            const sendCompletionSignal = (source) => {
              const currentSocket = socketRef.current;
              const currentRoomCode = roomCodeRef.current;
              if (currentSocket && currentRoomCode) {
                console.log(`📤 Emitting tts-complete signal for question (${source})`);
                currentSocket.emit('tts-complete', { roomCode: currentRoomCode, type: 'question' });
              }
            };
            
            utterance.onend = () => {
              clearTimeout(fallbackTimeout);
              window.questionTTSInProgress = false;
              console.log('✅ Question TTS complete (pre-loaded)');
              sendCompletionSignal('normal-completion');
            };
            
            utterance.onerror = () => {
              clearTimeout(fallbackTimeout);
              window.questionTTSInProgress = false;
              sendCompletionSignal('error-case');
            };
            
            // Start speaking immediately
            if ('speechSynthesis' in window) {
              speechSynthesis.speak(utterance);
            }
            return;
          } catch (error) {
            console.warn('⚠️ Pre-loaded TTS failed, falling back to normal TTS');
            preloadTTSRef.current = null;
            nextQuestionRef.current = null;
          }
        }
        
        // Send immediate signal if TTS isn't available
        if (!speakTTSRef.current) {
          console.warn('⚠️ TTS not available, signaling completion immediately');
          clearTimeout(fallbackTimeout);
          window.questionTTSInProgress = false;
          sendCompletionSignal('immediate-no-tts');
          return;
        }
        
        try {
          console.log('🔊 Speaking question:', currentQuestion.question.substring(0, 50));
          // Use natural voice for questions (different from instructions)
          await speakTTSRef.current(currentQuestion.question, { rate: 0.85, pitch: 0.95, priority: 'high' });
          console.log('✅ Question TTS complete');
          
          // Clear fallback timeout since TTS completed normally
          clearTimeout(fallbackTimeout);
          window.questionTTSInProgress = false;
          
          // Signal server that TTS is complete
          sendCompletionSignal('normal-completion');
        } catch (error) {
          console.error('TTS error:', error);
          
          // Clear fallback timeout since we're handling error
          clearTimeout(fallbackTimeout);
          window.questionTTSInProgress = false;
          
          // Signal server even on error so game doesn't hang
          sendCompletionSignal('error-case');
        }
      }, 200); // Reduced delay for faster start

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [currentQuestion, gameState, questionNumber, socket, roomCode]);

  const handleSelectPacks = (packIds) => {
    if (socket && roomCode) {
      socket.emit('select-packs', { roomCode, packIds });
      setShowSettings(false);
    }
  };

  const handleTogglePack = (packId) => {
    setSelectedPacks(prev => {
      if (prev.includes(packId)) {
        return prev.filter(id => id !== packId);
      } else {
        return [...prev, packId];
      }
    });
  };

  const handleSelectAllPacks = () => {
    const allPackIds = gamePacks.map(p => p.id);
    setSelectedPacks(allPackIds);
  };

  const handleClearAllPacks = () => {
    setSelectedPacks([]);
  };


  const handleTogglePause = () => {
    if (socket && roomCode) {
      socket.emit('toggle-pause', { roomCode });
    }
  };

  const handleSelectRoundCount = (count) => {
    setRoundCount(count);
    if (socket && roomCode) {
      socket.emit('select-round-count', { roomCode, roundCount: count });
    }
  };

  const handlePlayAgain = () => {
    window.location.reload();
  };

  // Fullscreen support for iPad screen mirroring
  const toggleFullscreen = async () => {
    const element = hostScreenRef.current || document.documentElement;
    
    try {
      if (!document.fullscreenElement && !document.webkitFullscreenElement && !document.mozFullScreenElement) {
        // Enter fullscreen
        if (element.requestFullscreen) {
          await element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          await element.webkitRequestFullscreen();
        } else if (element.mozRequestFullScreen) {
          await element.mozRequestFullScreen();
        } else if (element.msRequestFullscreen) {
          await element.msRequestFullscreen();
        }
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          await document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        }
      }
    } catch (err) {
      console.warn('Fullscreen error:', err);
    }
  };

  // Monitor fullscreen state
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        !!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement)
      );
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const sortedPlayers = [...players].sort((a, b) => b.score - a.score);

  const renderRoundBadge = () => {
    if (!currentRound) return null;
    const roundNumber = currentRoundIndex + 1;
    return (
      <div className="round-badge">
        <div className="round-badge-main">
          <span className="round-icon">{currentRound.icon || '⭐'}</span>
          <span className="round-label">
            Round {roundNumber} of {totalRounds || '?'}
          </span>
        </div>
        <div className="round-subtitle">
          {currentRound.label || 'Trivia Round'}
        </div>
        {currentRound.description && (
          <div className="round-description">
            {currentRound.description}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="host-screen" ref={hostScreenRef}>
      {/* Audio Enable Button - Show if audio not unlocked yet */}
      {!audioEnabled && gameState === 'LOBBY' && (
        <div className="audio-enable-overlay">
          <div className="audio-enable-card">
            <h2>🔊 Enable Audio</h2>
            <p>Click the button below to enable text-to-speech audio.</p>
            <p className="audio-hint">This is required for audio to work on iPad and other devices.</p>
            <button 
              className="btn-enable-audio"
              onClick={async () => {
                console.log('🔊 Enable Audio button clicked');
                await unlockAudio();
                // Audio is now enabled - no need to announce (causes repetition)
              }}
            >
              Enable Audio
            </button>
          </div>
        </div>
      )}

      {/* Join Toast Notification */}
      {joinToast && joinToast.visible && (
        <div className="join-toast">
          <div className="join-toast-content">
            <span className="join-toast-icon">🎉</span>
            <span className="join-toast-text">{joinToast.name} joined the game!</span>
          </div>
        </div>
      )}

      {/* Connection Status */}
      {socket && !socket.connected && (
        <div className="connection-error">
          <h2>⚠️ Not Connected to Server</h2>
          <p><strong>Server URL:</strong> {SERVER_URL}</p>
          <p><strong>Current Page:</strong> {window.location.href}</p>
          <p>Make sure the server is running:</p>
          <code>npm run server</code>
          <p>The server should be on port <strong>3002</strong></p>
          <p>Check the browser console (F12) for detailed connection errors.</p>
        </div>
      )}

      {/* Roulette Wheel Overlay - Side-scrolling pack selection */}
      {showWheelSpinner && wheelPack && wheelPack.allPacks && wheelPack.allPacks.length > 0 && (
        <div className="roulette-overlay">
          <div className="roulette-container">
            <div className="roulette-title">🎡 Selecting Topic...</div>
            
            {/* Roulette track with scrolling packs */}
            <div className="roulette-track-wrapper">
              <div 
                className="roulette-track"
                style={{
                  transform: `translateX(${roulettePosition}px)`,
                  transition: 'transform 0.05s linear'
                }}
              >
                {/* Repeat packs multiple times for seamless scrolling */}
                {[...Array(5)].map((_, cycle) => (
                  wheelPack.allPacks.map((pack, index) => {
                    const globalIndex = cycle * wheelPack.allPacks.length + index;
                    const isSelected = pack.id === wheelPack.packId;
                    const itemWidth = 200; // Width of each pack item
                    const spacing = 20; // Gap between items
                    const totalWidth = itemWidth + spacing;
                    // Calculate position: each item starts at its index * totalWidth, plus padding offset
                    const paddingOffset = window.innerWidth / 2 - itemWidth / 2; // Center offset
                    const itemPosition = globalIndex * totalWidth + paddingOffset;
                    const centerOffset = Math.abs(roulettePosition + itemPosition);
                    const isHighlighted = centerOffset < 150; // Highlight items near center
                    const isCentered = centerOffset < 50; // Selected item when centered
                    
                    return (
                      <div
                        key={`${cycle}-${pack.id}`}
                        className={`roulette-item ${isSelected && isCentered ? 'selected' : ''} ${isHighlighted ? 'highlighted' : ''}`}
                      >
                        <div className="roulette-item-icon">{pack.icon}</div>
                        <div className="roulette-item-name">{pack.name}</div>
                      </div>
                    );
                  })
                ))}
              </div>
              
              {/* Center indicator line */}
              <div className="roulette-indicator"></div>
            </div>
            
            {/* Selected pack display - show when animation completes */}
            {wheelPack.pack && Math.abs(roulettePosition + rouletteSelectedIndex * 220 + (window.innerWidth / 2 - 100)) < 50 && (
              <div className="roulette-result">
                <div className="roulette-result-icon">{wheelPack.pack.icon}</div>
                <div className="roulette-result-name">{wheelPack.pack.name}</div>
                <div className="roulette-result-text">
                  Round {wheelPack.roundIndex + 1} will feature: <strong>{wheelPack.pack.name}</strong>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Round Spinner Overlay - Show round instructions */}
      {/* Round-End Full Scoreboard */}
      {showRoundEndScoreboard && roundEndData && (
        <div className="round-end-scoreboard-overlay">
          <div className="round-end-scoreboard">
            <h2>🏆 Round {roundEndData.roundIndex + 1} Complete!</h2>
            <div className="scoreboard-standings">
              {roundEndData.scores.map((player, index) => {
                const isFirst = index === 0;
                const isLast = index === roundEndData.scores.length - 1 && roundEndData.scores.length > 3;
                return (
                  <div 
                    key={player.name} 
                    className={`standing-item ${isFirst ? 'first-place' : ''} ${isLast ? 'last-place' : ''}`}
                  >
                    <div className="standing-rank">
                      {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                    </div>
                    <div className="standing-name">
                      {player.name}
                      {player.streak > 0 && <span className="streak-badge">🔥{player.streak}</span>}
                    </div>
                    <div className="standing-score">{player.score} pts</div>
                    {isLast && (
                      <div className="last-place-joke">
                        😅 {getRandomJoke()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {!roundEndData.isLastRound && (
              <div className="next-round-hint">Next round starting soon...</div>
            )}
          </div>
        </div>
      )}

      {showRoundSpinner && spinnerRound && (
        <div className="round-spinner-overlay">
          <div className="round-spinner">
            <div className="round-title">
              Round {spinnerRound.roundIndex + 1} of {spinnerRound.totalRounds}
            </div>
            <div className="round-name">
              🎯 {spinnerRound.currentRound?.name || 'Next Round'}
            </div>
            <div className="round-instructions">
              <h3>How to Play:</h3>
              <p className="round-description">
                {spinnerRound.currentRound?.description || 'Get ready for the next round!'}
              </p>
              {spinnerRound.pack && (
                <div className="round-topic">
                  <p><strong>Topic:</strong> {spinnerRound.pack.icon} {spinnerRound.pack.name}</p>
                </div>
              )}
            </div>
            <div className="round-ready-text">Get Ready!</div>
          </div>
        </div>
      )}

      {/* Pause Overlay */}
      {isPaused && (
        <div className="pause-overlay">
          <div className="pause-content">
            <h1>⏸️ Game Paused</h1>
            <p>Paused by: {pausedBy}</p>
            <button className="btn-resume" onClick={handleTogglePause}>
              Resume Game
            </button>
          </div>
        </div>
      )}

      {/* Fullscreen Button */}
      <button 
        className="btn-fullscreen"
        onClick={toggleFullscreen}
        title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
        aria-label={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
      >
        {isFullscreen ? '⛶' : '⛶'}
      </button>

      {gameState === 'LOBBY' && (
        <div className="lobby-view">
          <div className="lobby-header">
            <h1>🎮 Buzzer Bash</h1>
            <button 
              className="btn-help"
              onClick={() => setShowTutorial(true)}
              title="How to Play"
            >
              ❓
            </button>
          </div>
          
          <div className="room-code-display">
            <h2>Join the Game!</h2>
            <div className="join-methods">
              {/* QR Code */}
              <div className="qr-code-container">
                <QRCodeSVG 
                  value={`${window.location.origin}/play?room=${roomCode}`}
                  size={180}
                  level="H"
                  includeMargin={true}
                  bgColor="#ffffff"
                  fgColor="#1a1a2e"
                  imageSettings={{
                    src: "",
                    height: 24,
                    width: 24,
                    excavate: true,
                  }}
                />
                <p className="qr-hint">Scan with your phone</p>
              </div>
              
              {/* Or divider */}
              <div className="join-divider">
                <span>OR</span>
              </div>
              
              {/* Room Code */}
              <div className="room-code-section">
                <p className="join-url">Go to:</p>
                <p className="join-url-large">{window.location.origin}/play</p>
                <p className="join-url">Enter code:</p>
                <div className="code">{roomCode}</div>
              </div>
            </div>
          </div>

          {/* Settings Button */}
          <div className="settings-section">
            <button 
              className="btn-settings"
              onClick={() => setShowSettings(true)}
            >
              ⚙️ Game Settings
            </button>
            {selectedPacks.length > 0 && (
              <div className="selected-packs-display">
                <p><strong>Selected Topics ({selectedPacks.length}):</strong></p>
                <div className="selected-packs-list">
                  {selectedPacks.map(packId => {
                    const pack = gamePacks.find(p => p.id === packId);
                    return pack ? (
                      <span key={packId} className="pack-tag">
                        {pack.icon} {pack.name}
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Settings Modal */}
          {showSettings && (
            <div className="settings-modal-overlay" onClick={() => setShowSettings(false)}>
              <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
                <h2>⚙️ Game Settings</h2>
                <div className="settings-content">
                    <div className="pack-selection-settings">
                    <h3>Select Topics for This Game</h3>
                    <p className="settings-hint">
                      Unselect topics you DON'T want. A random topic will be selected for each round.
                      <br />
                      <strong>{selectedPacks.length} of {gamePacks.length} topics selected</strong>
                    </p>
                    <div className="settings-quick-actions">
                      <button 
                        className="btn-select-all"
                        onClick={handleSelectAllPacks}
                      >
                        ✓ Select All
                      </button>
                      <button 
                        className="btn-clear-all"
                        onClick={handleClearAllPacks}
                      >
                        ✗ Clear All
                      </button>
                    </div>
                    <div className="pack-grid">
                      {gamePacks.length === 0 ? (
                        <p>Loading packs...</p>
                      ) : (
                        gamePacks.map((pack) => (
                          <div
                            key={pack.id}
                            className={`pack-card ${selectedPacks.includes(pack.id) ? 'selected' : 'unselected'}`}
                            onClick={() => handleTogglePack(pack.id)}
                          >
                            <div className="pack-icon">{pack.icon}</div>
                            <div className="pack-name">{pack.name}</div>
                            <div className="pack-description">{pack.description}</div>
                            {selectedPacks.includes(pack.id) ? (
                              <div className="pack-checkmark">✓</div>
                            ) : (
                              <div className="pack-uncheckmark">✗</div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                    <div className="settings-actions">
                      <button 
                        className="btn-save-settings"
                        onClick={() => handleSelectPacks(selectedPacks)}
                        disabled={selectedPacks.length === 0}
                      >
                        Save Settings ({selectedPacks.length} selected)
                      </button>
                      <button 
                        className="btn-cancel-settings"
                        onClick={() => setShowSettings(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="players-list">
            <h3>Players ({players.length}/10)</h3>
            {players.length === 0 ? (
              <p className="no-players">Waiting for players to join...</p>
            ) : (
              <ul>
                {players.map((player, index) => (
                  <li key={index}>{player.name}</li>
                ))}
              </ul>
            )}
          </div>

          {/* Round Count Selection */}
          <div className="round-count-selection">
            <h3>How many rounds?</h3>
            <div className="round-count-buttons">
              {[3, 5, 6, 8].map(count => (
                <button
                  key={count}
                  className={`round-count-button ${roundCount === count ? 'selected' : ''}`}
                  onClick={() => handleSelectRoundCount(count)}
                >
                  {count}
                </button>
              ))}
            </div>
            <p className="round-count-hint">
              Rounds will be chosen at random, ending with a Final Countdown.
            </p>
          </div>

          {/* Vote to Start */}
          {selectedPacks.length > 0 && players.length > 0 && (
            <div className="vote-section">
              <h3>Vote to Start</h3>
              <p className="vote-count">
                {startVotes} / {requiredVotes} votes needed
              </p>
              <div className="vote-progress">
                <div 
                  className="vote-progress-bar"
                  style={{ width: `${(startVotes / requiredVotes) * 100}%` }}
                />
              </div>
              <p className="vote-hint">Players can vote to start from their phones</p>
            </div>
          )}
          
          {selectedPacks.length === 0 && (
            <div className="settings-prompt">
              <p>⚠️ Please select at least one topic in Settings before starting the game.</p>
            </div>
          )}
        </div>
      )}

      {(gameState === 'QUESTION_INTRO' || gameState === 'ANSWERING' || gameState === 'REVEAL') && currentQuestion && (
        <div className="game-view">
          {/* Pause Button */}
          <button className="btn-pause" onClick={handleTogglePause}>
            {isPaused ? '▶️ Resume' : '⏸️ Pause'}
          </button>

          {/* Countdown Timer */}
          {countdown !== null && !isPaused && (
            <div className="countdown-timer">
              {countdown}
            </div>
          )}

          {renderRoundBadge()}

          <div className="question-header">
            <span className="question-number">Question {questionNumber} of {totalQuestions}</span>
          </div>

          {gameState === 'QUESTION_INTRO' && (
            <div className="question-intro">
              <h2 className="question-text">{currentQuestion.question}</h2>
              <div className="answer-options preview">
                {currentQuestion.answers.map((answer, index) => (
                  <div key={index} className="answer-option">
                    {String.fromCharCode(65 + index)}. {answer}
                  </div>
                ))}
              </div>
              <p className="wait-message">Get ready...</p>
            </div>
          )}

          {gameState === 'ANSWERING' && (
            <div className={`answering-view mode-${currentRound?.mode || 'quickfire'}`}>
              {/* Mode-specific indicators */}
              {currentRound?.mode === 'bomb_pass' && bombHolder && (
                <div className="bomb-indicator">
                  <div className="bomb-icon">💣</div>
                  <div className="bomb-text">
                    <strong>{bombHolder}</strong> holds the bomb!
                  </div>
                </div>
              )}
              
              <h2 className="question-text">{currentQuestion.question}</h2>
              <div className="answer-options">
                {currentQuestion.answers.map((answer, index) => (
                  <div key={index} className="answer-option">
                    {String.fromCharCode(65 + index)}. {answer}
                  </div>
                ))}
              </div>
              <div className="answered-players">
                <h3>Answered:</h3>
                {buzzedPlayers.length === 0 ? (
                  <p>{questionReady ? 'Waiting for players to answer...' : 'Question loading...'}</p>
                ) : (
                  <ul>
                    {buzzedPlayers.map((bp, index) => (
                      <li key={index} className="answered-player-item">
                        #{bp.order} - {bp.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              
              {/* Scoreboard at bottom */}
              <div className="scoreboard-bottom">
                <h3>Live Scores</h3>
                <div className="scoreboard-grid">
                  {sortedPlayers.map((player, index) => {
                    const isLeader = index === 0 && player.score > 0;
                    const isBombHolder = currentRound?.mode === 'bomb_pass' && bombHolder === player.name;
                    const isEliminated = eliminatedPlayers.includes(player.name);
                    return (
                      <div 
                        key={index} 
                        className={`scoreboard-item ${isLeader ? 'leader' : ''} ${isBombHolder ? 'bomb-holder' : ''} ${isEliminated ? 'eliminated' : ''}`}
                      >
                        <span className="rank-badge">{index + 1}</span>
                        <span className="player-name">
                          {player.name}
                          {isLeader && <span className="leader-crown">👑</span>}
                          {isBombHolder && <span className="bomb-badge">💣</span>}
                        </span>
                        <span className={`player-score ${player.score < 0 ? 'negative' : ''}`}>
                          {player.score}
                        </span>
                        {player.streak > 0 && <span className="streak-badge">🔥{player.streak}</span>}
                        {/* Health bar for Final Countdown */}
                        {currentRound?.mode === 'final_countdown' && revealData?.health && revealData.health[player.name] !== undefined && (
                          <div className="health-bar-container">
                            <div 
                              className="health-bar"
                              style={{ width: `${Math.max(0, Math.min(100, (revealData.health[player.name] / 10) * 100))}%` }}
                            />
                            <span className="health-text">{revealData.health[player.name]}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {gameState === 'REVEAL' && revealData && (
            <div className={`reveal-view mode-${currentRound?.mode || 'quickfire'}`}>
              {/* Point Steal Animation */}
              {stealAnimation && (
                <div className="steal-animation">
                  <div className="steal-content">
                    <div className="steal-icon">💰</div>
                    <div className="steal-text">
                      <strong>{stealAnimation.to}</strong> stole <strong>{stealAnimation.amount}</strong> points from <strong>{stealAnimation.from}</strong>!
                    </div>
                  </div>
                </div>
              )}
              
              {/* Eliminated Players Notice */}
              {revealData.eliminated && revealData.eliminated.length > 0 && (
                <div className="elimination-notice">
                  <div className="elimination-content">
                    <div className="elimination-icon">💀</div>
                    <div className="elimination-text">
                      <strong>{revealData.eliminated.join(', ')}</strong> {revealData.eliminated.length === 1 ? 'has been eliminated' : 'have been eliminated'}!
                    </div>
                  </div>
                </div>
              )}
              
              <h2 className="question-text">{currentQuestion.question}</h2>
              <div className="answer-options reveal">
                {currentQuestion.answers.map((answer, index) => (
                  <div
                    key={index}
                    className={`answer-option ${index === revealData.correctAnswer ? 'correct' : ''}`}
                  >
                    {String.fromCharCode(65 + index)}. {answer}
                    {index === revealData.correctAnswer && ' ✓'}
                  </div>
                ))}
              </div>
              {/* Full Scoreboard After Question */}
              <div className="scoreboard-full">
                <h3>📊 Current Standings</h3>
                <div className="scoreboard-full-grid">
                  {players
                    .slice()
                    .sort((a, b) => b.score - a.score)
                    .map((player, index) => {
                      const result = revealData.results?.find(r => r.name === player.name);
                      const isLeader = index === 0 && player.score > 0;
                      const isEliminated = revealData.eliminated && revealData.eliminated.includes(player.name);
                      return (
                        <div 
                          key={player.name} 
                          className={`scoreboard-full-item ${result?.isCorrect ? 'correct-flash' : ''} ${isLeader ? 'leader-item' : ''} ${isEliminated ? 'eliminated-item' : ''}`}
                        >
                          <div className="score-rank">{index + 1}</div>
                          <div className="score-name">
                            {player.name}
                            {isLeader && <span className="leader-crown">👑</span>}
                          </div>
                          <div className="score-points">{player.score}</div>
                          {result && (
                            <div className={`score-change ${result.isCorrect ? 'positive' : 'negative'}`}>
                              {result.isCorrect ? `+${result.points}` : result.points}
                            </div>
                          )}
                          {result?.answerTime && (
                            <div className="answer-time">⏱️ {result.answerTime}s</div>
                          )}
                          {player.streak > 0 && (
                            <div className="score-streak">🔥 {player.streak}</div>
                          )}
                          {/* Health bar for Final Countdown */}
                          {currentRound?.mode === 'final_countdown' && revealData.health && revealData.health[player.name] !== undefined && (
                            <div className="health-bar-container-full">
                              <div className="health-label">Health:</div>
                              <div className="health-bar-wrapper">
                                <div 
                                  className={`health-bar-full ${revealData.health[player.name] <= 0 ? 'empty' : ''}`}
                                  style={{ width: `${Math.max(0, Math.min(100, (revealData.health[player.name] / 10) * 100))}%` }}
                                />
                                <span className="health-text-full">{revealData.health[player.name]}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {gameState === 'GAME_END' && finalScores && (
        <div className="game-end-view">
          <h1>🎉 Game Over!</h1>
          <div className="final-scoreboard">
            <h2>Final Scores</h2>
            {finalScores.map((player, index) => (
              <div key={index} className={`final-score-item ${index === 0 ? 'winner' : ''}`}>
                <span className="rank">#{index + 1}</span>
                <span className="name">{player.name}</span>
                <span className="score">{player.score} pts</span>
                {player.streak > 0 && <span className="streak">🔥 {player.streak} streak</span>}
              </div>
            ))}
          </div>
          <button className="btn-play-again" onClick={handlePlayAgain}>
            Play Again
          </button>
          <button className="btn-back" onClick={() => navigate('/')}>
            Back to Home
          </button>
        </div>
      )}
      
      {/* Tutorial Modal */}
      {showTutorial && (
        <Tutorial onClose={() => setShowTutorial(false)} isHost={true} />
      )}
    </div>
  );
}

export default HostScreen;
